import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ReferralCallMetrics {
  scheduled: number;
  realized: number;
  answered: number;
  withReferral: number;
  missed: number;
  noAnswerBusy: number;
  askedButNoReferral: number;
  didNotAsk: number;
}

export interface UserReferralMetrics {
  userId: string;
  userName: string;
  scheduled: number;
  realized: number;
  answered: number;
  withReferral: number;
  missed: number;
  realizationRate: number;
  answerRate: number;
  conversionRate: number;
}

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

export function useReferralCallMetrics(dateRange?: DateRange) {
  return useQuery({
    queryKey: ['referral-call-metrics', dateRange?.from?.toISOString(), dateRange?.to?.toISOString()],
    queryFn: async () => {
      // Build date filter
      let query = supabase
        .from('calls')
        .select(`
          id,
          status,
          call_attempt_result,
          referral_asked,
          referral_received,
          handled_by_app_user_id,
          scheduled_for,
          completed_at,
          app_users!calls_handled_by_app_user_id_fkey (
            id,
            display_name,
            first_name,
            last_name
          )
        `)
        .eq('call_purpose', 'referral_request');

      if (dateRange?.from) {
        query = query.gte('scheduled_for', dateRange.from.toISOString());
      }
      if (dateRange?.to) {
        query = query.lte('scheduled_for', dateRange.to.toISOString());
      }

      const { data: calls, error } = await query;

      if (error) throw error;

      // Calculate global metrics
      const scheduled = calls?.length || 0;
      const realized = calls?.filter(c => 
        c.status === 'completed' || c.status === 'missed' || 
        ['answered', 'no_answer', 'busy', 'voicemail'].includes(c.call_attempt_result || '')
      ).length || 0;
      const answered = calls?.filter(c => c.call_attempt_result === 'answered').length || 0;
      const withReferral = calls?.filter(c => c.referral_received === true).length || 0;
      const missed = calls?.filter(c => c.status === 'missed').length || 0;
      const noAnswerBusy = calls?.filter(c => 
        ['no_answer', 'busy', 'voicemail'].includes(c.call_attempt_result || '')
      ).length || 0;
      const askedButNoReferral = calls?.filter(c => 
        c.referral_asked === true && c.referral_received === false
      ).length || 0;
      const didNotAsk = calls?.filter(c => 
        c.call_attempt_result === 'answered' && c.referral_asked === false
      ).length || 0;

      const globalMetrics: ReferralCallMetrics = {
        scheduled,
        realized,
        answered,
        withReferral,
        missed,
        noAnswerBusy,
        askedButNoReferral,
        didNotAsk,
      };

      // Calculate per-user metrics
      const userMetricsMap = new Map<string, UserReferralMetrics>();

      calls?.forEach(call => {
        const userId = call.handled_by_app_user_id;
        if (!userId) return;

        const user = call.app_users as any;
        const userName = user?.display_name || 
          `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 
          'Unknown';

        if (!userMetricsMap.has(userId)) {
          userMetricsMap.set(userId, {
            userId,
            userName,
            scheduled: 0,
            realized: 0,
            answered: 0,
            withReferral: 0,
            missed: 0,
            realizationRate: 0,
            answerRate: 0,
            conversionRate: 0,
          });
        }

        const metrics = userMetricsMap.get(userId)!;
        metrics.scheduled++;

        if (call.status === 'completed' || call.status === 'missed' || 
            ['answered', 'no_answer', 'busy', 'voicemail'].includes(call.call_attempt_result || '')) {
          metrics.realized++;
        }

        if (call.call_attempt_result === 'answered') {
          metrics.answered++;
        }

        if (call.referral_received === true) {
          metrics.withReferral++;
        }

        if (call.status === 'missed') {
          metrics.missed++;
        }
      });

      // Calculate rates
      const userMetrics = Array.from(userMetricsMap.values()).map(m => ({
        ...m,
        realizationRate: m.scheduled > 0 ? (m.realized / m.scheduled) * 100 : 0,
        answerRate: m.realized > 0 ? (m.answered / m.realized) * 100 : 0,
        conversionRate: m.answered > 0 ? (m.withReferral / m.answered) * 100 : 0,
      }));

      return {
        globalMetrics,
        userMetrics: userMetrics.sort((a, b) => b.scheduled - a.scheduled),
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}
